import { Table, Box, TextFilter, Button } from "@cloudscape-design/components";
import TableHeader from "../common/components/elements/TableHeader";
import usePaginationHandler from "../common/hooks/PaginationHandler";
import {
  useTablePreferences,
  ColumnDefinition,
} from "../common/components/elements/TablePreferences";
import { Role } from "../openapi-generated/lmpSchemas";
import { fetchGetRoles, fetchDeleteUser } from "../openapi-generated/lmpComponents";
import CreateEditAccountModal from "../components/modals/CreateEditAccount";
import { useEffect, useState } from "react";
import ConfirmModal from "../common/components/elements/ConfirmModal";
import { useGlobal } from "../common/hooks/GlobalContext";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useNotificationBar } from "../common/hooks/NotificationsBar";
import { fetchAllPages, toCsv, downloadCsvFile } from "../common/hooks/Utils";

const getTextFilterCounterText = (count: number) => `${count} ${count === 1 ? "match" : "matches"}`;

const UsersPage = () => {
  const { globalUser } = useGlobal();
  const navigate = useNavigate();

  if (globalUser?.Role !== "Admin") {
    navigate("/campaigns");
  }

  const [email, setEmail] = useState("");
  const notificationBar = useNotificationBar();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [filteringText, setFilteringText] = useState("");

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState<string | null>(null);

  const columnDefinitions: ColumnDefinition<any>[] = [
    {
      id: "user_id",
      header: "UserID",
      visible: true,
      cell: (account) => (
        <Button
          variant="link"
          onClick={() => {
            setSelectedItems([account]);
            setEditModalVisible(true);
            return undefined;
          }}
        >
          {account.UserID}
        </Button>
      ),
    },
    {
      id: "name",
      header: "Name",
      visible: true,
      cell: (account) => account.Name || "",
    },
    {
      id: "surname",
      header: "Surname",
      visible: true,
      cell: (account) => account.Surname || "",
    },
    {
      id: "role",
      header: "Role",
      visible: true,
      cell: (account) => (account.Role === "NONE" ? "-" : account.Role || "-"),
    },
    {
      id: "area",
      header: "Area",
      visible: true,
      cell: (account) => (account.Role === "Admin" ? "-" : account.Area || "-"),
    },

    {
      id: "delete",
      header: "Delete",
      visible: true,
      cell: (account) => (
        <Button
          variant="icon"
          iconName="remove"
          ariaLabel={`Delete ${account.UserID}`}
          onClick={() => {
            setSelectedUserToDelete(account.UserID);
            setShowDeleteConfirm(true);
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    const getEmail = async () => {
      const data = (await Auth.currentSession()).getIdToken().decodePayload();
      setEmail(data.email);
    };
    getEmail();
  }, []);

  const [selectedItems, setSelectedItems] = useState<Role[]>([]);

  const { collectionPreferences, preferences } = useTablePreferences({
    pageSize: 20,
    contentDensity: "comfortable",
    columnDefinitions,
  });

  const { pagination, currentPage, isLoading, resetPagination } = usePaginationHandler({
    api: fetchGetRoles,
    pageSize: preferences.pageSize || 20,
    preInitialize: true,
  });

  const baseRows: Role[] = (currentPage ?? []).filter((u) => u.UserID !== email);

  const applyTextFilter = (rows: Role[], text: string): Role[] => {
    const q = (text || "").trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((u) => {
      const values = [u.UserID, u.Name, u.Surname, u.Role, u.Area]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());

      return values.some((v) => v.includes(q));
    });
  };

  const uiFilteredRows = applyTextFilter(baseRows, filteringText);
  const filteredItemsCount = uiFilteredRows.length;

  const onDownloadUsers = async () => {
    try {
      setDownloadLoading(true);
      notificationBar.clear();
      notificationBar.push({
        content: "Downloading users CSV...",
        dismissible: false,
        loading: true,
        type: "info",
      });

      const allUsers = await fetchAllPages<Role>(fetchGetRoles as any, {});
      const allUsersNoSelf = allUsers.filter((u) => u.UserID !== email);
      const allFilteredUsers = applyTextFilter(allUsersNoSelf, filteringText);

      const userCsvMap: Record<string, { header: string; value: (u: Role) => any }> = {
        user_id: { header: "UserID", value: (u) => u.UserID },
        name: { header: "Name", value: (u) => u.Name || "" },
        surname: { header: "Surname", value: (u) => u.Surname || "" },
        role: {
          header: "Role",
          value: (u) => (u.Role === "NONE" ? "-" : u.Role || "-"),
        },
        area: {
          header: "Area",
          value: (u) => (u.Role === "Admin" ? "-" : u.Area || "-"),
        },
      };

      const uiVisibleColumnIds = ((preferences.visibleContent || []) as string[]).filter((id) =>
        columnDefinitions.some((c) => c.id === id && c.visible),
      );

      const csvCols = uiVisibleColumnIds.filter((id) => userCsvMap[id]).map((id) => userCsvMap[id]);

      const finalCsvCols = csvCols.length
        ? csvCols
        : [userCsvMap.user_id, userCsvMap.name, userCsvMap.surname];

      const csvRows = allFilteredUsers.map((u) => {
        const row: Record<string, any> = {};
        finalCsvCols.forEach((col) => {
          row[col.header] = col.value(u);
        });
        return row;
      });

      const csv = toCsv(
        csvRows,
        finalCsvCols.map((c) => c.header),
      );

      const timestamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);

      downloadCsvFile(csv, `lmp_users_${timestamp}.csv`);

      notificationBar.clear();
      notificationBar.push({
        content: `Downloaded ${allFilteredUsers.length} users`,
        dismissible: true,
        type: "success",
      });
    } catch (error: any) {
      const message = error?.stack?.message || error?.message || `Unexpected error: ${error}`;
      notificationBar.clear();
      notificationBar.push({
        content: message,
        dismissible: true,
        type: "error",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUserToDelete) return;

    try {
      await fetchDeleteUser({
        pathParams: {
          userId: selectedUserToDelete,
        },
      } as any);

      notificationBar.push({
        content: "User deleted successfully",
        dismissible: true,
        type: "success",
      });

      setShowDeleteConfirm(false);
      setSelectedUserToDelete(null);
      resetPagination();
    } catch (error: any) {
      notificationBar.push({
        content: error?.message || "Unable to delete user",
        dismissible: true,
        type: "error",
      });
    }
  };

  const [editModalVisible, setEditModalVisible] = useState(false);

  const editModal = selectedItems.length && (
    <CreateEditAccountModal
      edit={true}
      userID={selectedItems[0].UserID}
      name={selectedItems[0].Name}
      surname={selectedItems[0].Surname}
      role={selectedItems[0].Role}
      area={selectedItems[0].Area}
      onClose={() => setEditModalVisible(false)}
      onConfirm={() => {
        setEditModalVisible(false);
        setSelectedItems([]);
        resetPagination();
      }}
    />
  );

  useEffect(() => {
    setSelectedItems([]);
  }, []);

  return (
    <>
      {editModalVisible && editModal}

      {/* Start Change *********************** */}
      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete User"
          warn
          onConfirm={() => {
            void deleteUser();
          }}
          onClose={() => setShowDeleteConfirm(false)}
        >
          Are you sure you want to delete "{selectedUserToDelete}"?
        </ConfirmModal>
      )}
      {/* End Change *********************** */}

      <Table
        variant="full-page"
        trackBy="UserID"
        stickyHeader
        header={
          <TableHeader
            itemsCount={filteredItemsCount}
            actions={
              [
                {
                  label: "Download CSV",
                  iconName: "download",
                  disabled: isLoading || downloadLoading,
                  onClick: () => onDownloadUsers(),
                },
              ] as any
            }
          >
            Users
          </TableHeader>
        }
        loading={isLoading}
        contentDensity={preferences.contentDensity}
        visibleColumns={preferences.visibleContent}
        columnDefinitions={columnDefinitions}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        selectedItems={selectedItems}
        items={uiFilteredRows}
        loadingText="Loading users"
        empty={
          <Box variant="p" color="text-status-info" textAlign="center">
            No users to display.
          </Box>
        }
        filter={
          <TextFilter
            filteringText={filteringText}
            onChange={({ detail }) => setFilteringText(detail.filteringText)}
            filteringAriaLabel="Filter users"
            filteringPlaceholder="Find users"
            filteringClearAriaLabel="Clear"
            countText={getTextFilterCounterText(filteredItemsCount)}
          />
        }
        pagination={pagination}
        preferences={collectionPreferences}
      />
    </>
  );
};

export default UsersPage;
